import { useEffect, useState, useCallback } from "react";
import api, { apiErr, API } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { money, fmtDate } from "@/lib/format";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { Loader2, Download, FileText, History, CheckCircle2 } from "lucide-react";

const STAGES = [
  ["assigned_job", "Assigned Job"], ["search_existing", "Search Existing"], ["import_create", "Import / Create"],
  ["resume_review", "Resume Review"], ["screening", "Screening"], ["confirm_interest", "Confirm Interest"],
  ["record_consent", "Record Consent"], ["ready_to_submit", "Ready to Submit"],
];

export const NC = (v) => (v === null || v === undefined || v === "" ? "Not Confirmed" : v);

export function CandidateWorkflow({ candidate, onChanged }) {
  const { can } = useAuth();
  const [jobs, setJobs] = useState([]);
  const [jobId, setJobId] = useState(candidate.workflow?.job_id || "");
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  const wf = candidate.workflow || {};
  const currentIdx = STAGES.findIndex(([s]) => s === wf.stage);

  useEffect(() => { api.get("/jobs", { params: { page_size: 100 } }).then(({ data }) => setJobs(data.items || [])).catch(() => {}); }, []);

  const move = async (stage) => {
    setBusy(true);
    try {
      await api.post(`/candidates/${candidate.id}/workflow`, { stage, job_id: jobId || null, note: note || null });
      toast.success("Workflow updated"); setNote(""); onChanged?.();
    } catch (e) { toast.error(apiErr(e)); } finally { setBusy(false); }
  };

  return (
    <div className="space-y-4" data-testid="candidate-workflow">
      <Card className="p-5">
        <div className="mb-4 font-display font-semibold">Recruiting workflow</div>
        <div className="flex flex-wrap gap-2">
          {STAGES.map(([s, l], i) => (
            <div key={s} className={`flex items-center gap-1.5 rounded-full border px-3 py-1.5 text-xs ${i === currentIdx ? "border-[#255058] bg-[#255058] text-white" : i < currentIdx ? "border-emerald-200 bg-emerald-50 text-emerald-800" : "border-border text-muted-foreground"}`} data-testid={`wf-stage-${s}`}>
              {i < currentIdx && <CheckCircle2 className="h-3 w-3" />}{l}
            </div>
          ))}
        </div>
        {can("candidate:edit") && (
          <div className="mt-4 grid gap-3 sm:grid-cols-3">
            <div className="space-y-1.5"><Label className="text-xs">Assigned job</Label>
              <Select value={jobId || "none"} onValueChange={(v) => setJobId(v === "none" ? "" : v)}>
                <SelectTrigger data-testid="wf-job"><SelectValue placeholder="Not linked" /></SelectTrigger>
                <SelectContent><SelectItem value="none">Not linked</SelectItem>{jobs.map((j) => <SelectItem key={j.id} value={j.id}>{j.title} — {j.client_name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5"><Label className="text-xs">Move to stage</Label>
              <Select onValueChange={move}>
                <SelectTrigger data-testid="wf-move"><SelectValue placeholder="Select stage" /></SelectTrigger>
                <SelectContent>{STAGES.map(([s, l]) => <SelectItem key={s} value={s}>{l}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5"><Label className="text-xs">Note</Label><Input data-testid="wf-note" value={note} onChange={(e) => setNote(e.target.value)} /></div>
          </div>
        )}
        {busy && <Loader2 className="mt-3 h-4 w-4 animate-spin text-[#255058]" />}
      </Card>

      <Card className="p-5">
        <div className="mb-3 flex items-center gap-2 font-display font-semibold"><History className="h-4 w-4 text-[#255058]" /> Stage history</div>
        <div className="space-y-2">
          {(wf.history || []).slice().reverse().map((h, i) => (
            <div key={i} className="text-sm"><span className="font-medium">{(STAGES.find(([s]) => s === h.stage) || [])[1] || h.stage}</span>
              <span className="text-xs text-muted-foreground"> · {h.by} · {fmtDate(h.at)}{h.job_title ? ` · ${h.job_title}` : ""}{h.note ? ` · ${h.note}` : ""}</span></div>
          ))}
          {!(wf.history || []).length && <div className="text-sm text-muted-foreground">No workflow history yet.</div>}
        </div>
      </Card>

      <Card className="p-5">
        <div className="mb-3 font-display font-semibold">Screening & availability</div>
        <dl className="grid gap-x-8 gap-y-2 text-sm sm:grid-cols-2">
          <Row k="Availability" v={candidate.availability} />
          <Row k="Interview availability" v={candidate.interview_availability} />
          <Row k="Time zone" v={candidate.time_zone} />
          <Row k="Notice period" v={candidate.notice_period} />
          <Row k="Willing to relocate" v={candidate.willing_to_relocate === null || candidate.willing_to_relocate === undefined ? null : candidate.willing_to_relocate ? "Yes" : "No"} />
          <Row k="Willing to travel" v={candidate.willing_to_travel === null || candidate.willing_to_travel === undefined ? null : candidate.willing_to_travel ? "Yes" : "No"} />
          <Row k="Work preference" v={candidate.work_model} />
          <Row k="Consent" v={candidate.consent_status} />
          <Row k="Consent date" v={candidate.consent_date ? fmtDate(candidate.consent_date) : null} />
          <Row k="Consent method" v={candidate.consent_method} />
        </dl>
        {!!(candidate.screening_answers || []).length && (
          <div className="mt-4 space-y-2">
            <div className="text-xs font-semibold uppercase tracking-wider text-[#255058]">Screening answers</div>
            {candidate.screening_answers.map((a, i) => (
              <div key={i} className="rounded-lg border border-border p-2.5 text-sm" data-testid={`screening-answer-${i}`}>
                <div className="font-medium">{a.question}</div><div className="text-muted-foreground">{NC(a.answer)}</div>
              </div>
            ))}
          </div>
        )}
      </Card>
    </div>
  );
}

export function ResumeVersions({ candidateId }) {
  const [data, setData] = useState(null);
  const load = useCallback(async () => {
    try { const { data } = await api.get(`/candidates/${candidateId}/resume/versions`); setData(data); }
    catch { setData({ items: [] }); }
  }, [candidateId]);
  useEffect(() => { load(); }, [load]);
  if (!data) return null;
  return (
    <div className="mt-4" data-testid="resume-versions">
      <div className="mb-2 text-xs font-semibold uppercase tracking-wider text-[#255058]">Resume versions ({data.items.length})</div>
      <div className="space-y-2">
        {data.items.map((v) => (
          <div key={v.document_id} className="flex items-center gap-3 rounded-lg border border-border p-2.5 text-sm" data-testid={`resume-version-${v.version}`}>
            <FileText className="h-4 w-4 text-[#255058]" />
            <div className="min-w-0 flex-1"><div className="truncate font-medium">{v.filename}</div>
              <div className="text-xs text-muted-foreground">v{v.version} · {fmtDate(v.uploaded_at)}{v.uploaded_by ? ` · ${v.uploaded_by}` : ""}</div></div>
            {v.version === data.current_version && <Badge variant="outline" className="text-[10px]">Current</Badge>}
            <a href={`${API}/candidates/${candidateId}/resume/versions/${v.document_id}/download`} target="_blank" rel="noreferrer">
              <Button size="sm" variant="outline" className="gap-1.5"><Download className="h-3.5 w-3.5" /> Open</Button>
            </a>
          </div>
        ))}
        {!data.items.length && <div className="text-sm text-muted-foreground">No resume versions yet.</div>}
      </div>
    </div>
  );
}

export function CompensationPanel({ candidateId, canEdit }) {
  const [d, setD] = useState(null);
  const [form, setForm] = useState({ amount: "", note: "" });
  const [offerForm, setOfferForm] = useState({ amount: "", note: "" });
  const [finalForm, setFinalForm] = useState({ amount: "", note: "" });
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try { const { data } = await api.get(`/candidates/${candidateId}/compensation`); setD(data); }
    catch (e) { toast.error(apiErr(e)); setD({}); }
  }, [candidateId]);
  useEffect(() => { load(); }, [load]);

  const post = async (url, body, label) => {
    if (!body.amount) { toast.error("Enter an amount"); return; }
    setBusy(true);
    try { await api.post(url, { amount: Number(body.amount), note: body.note || null }); toast.success(`${label} recorded`); load(); }
    catch (e) { toast.error(apiErr(e)); } finally { setBusy(false); }
  };

  if (!d) return <Card className="p-5 text-sm text-muted-foreground">Loading compensation…</Card>;
  const exp = d.candidate_expectation || {};
  return (
    <div className="grid gap-4 md:grid-cols-2" data-testid="compensation-panel">
      <Block title="Job Range" testid="comp-job-range"
        value={d.job_range ? `${money(d.job_range.min, d.job_range.currency)} – ${money(d.job_range.max, d.job_range.currency)}` : "Not Confirmed"}
        sub={d.job_range?.job_title} history={d.job_range?.history} render={(h) => `${money(h.min, h.currency)} – ${money(h.max, h.currency)}`} />

      <Block title="Candidate Expectation" testid="comp-expectation"
        value={exp.restricted ? "Restricted" : exp.current != null ? money(exp.current, exp.currency) : "Not Confirmed"}
        sub={exp.frequency} history={exp.history} render={(h) => money(h.amount, h.currency)}>
        {canEdit && !exp.restricted && (
          <Inline value={form} setValue={setForm} busy={busy} testid="comp-expectation-form"
            onSave={() => post(`/candidates/${candidateId}/compensation/expectation`, form, "Expectation")} />
        )}
      </Block>

      <Block title="Offer Amount" testid="comp-offer"
        value={d.offer?.offer_amount != null ? money(d.offer.offer_amount, d.offer.currency) : "Not Confirmed"}
        sub={d.offer ? `Offer status: ${d.offer.status || "—"}` : "No offer record yet"}
        history={d.offer?.offer_history} render={(h) => money(h.amount, h.currency)}>
        {canEdit && d.offer && (
          <Inline value={offerForm} setValue={setOfferForm} busy={busy} testid="comp-offer-form"
            onSave={() => post(`/offers/${d.offer.offer_id}/offer-amount`, offerForm, "Offer amount")} />
        )}
      </Block>

      <Block title="Final Accepted Amount" testid="comp-final"
        value={d.offer?.final_accepted_amount != null ? money(d.offer.final_accepted_amount, d.offer.currency) : "Not Confirmed"}
        sub={d.offer ? "Copied to the placement when it is created" : "No offer record yet"}
        history={d.offer?.final_history} render={(h) => money(h.amount, h.currency)}>
        {canEdit && d.offer && (
          <Inline value={finalForm} setValue={setFinalForm} busy={busy} testid="comp-final-form"
            onSave={() => post(`/offers/${d.offer.offer_id}/final-accepted`, finalForm, "Final accepted amount")} />
        )}
      </Block>
    </div>
  );
}

function Inline({ value, setValue, onSave, busy, testid }) {
  return (
    <div className="mt-3 flex flex-wrap gap-2" data-testid={testid}>
      <Input type="number" className="w-32" placeholder="Amount" value={value.amount} onChange={(e) => setValue({ ...value, amount: e.target.value })} data-testid={`${testid}-amount`} />
      <Input className="flex-1" placeholder="Note (optional)" value={value.note} onChange={(e) => setValue({ ...value, note: e.target.value })} />
      <Button size="sm" className="bg-[#255058] text-white hover:bg-[#1c3d43]" disabled={busy} onClick={onSave} data-testid={`${testid}-save`}>
        {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Record"}
      </Button>
    </div>
  );
}

function Block({ title, value, sub, history, render, children, testid }) {
  const [open, setOpen] = useState(false);
  return (
    <Card className="p-5" data-testid={testid}>
      <div className="text-xs font-semibold uppercase tracking-wider text-[#255058]">{title}</div>
      <div className="mt-1 font-display text-2xl font-bold">{value}</div>
      {sub && <div className="text-xs text-muted-foreground">{sub}</div>}
      {children}
      <button className="mt-3 text-xs text-muted-foreground underline" onClick={() => setOpen(!open)} data-testid={`${testid}-history-toggle`}>
        {open ? "Hide" : "Show"} history ({(history || []).length})
      </button>
      {open && (
        <div className="mt-2 space-y-1.5">
          {(history || []).slice().reverse().map((h, i) => (
            <div key={i} className="text-xs"><span className="font-medium">{render(h)}</span> · {h.by || "—"} · {fmtDate(h.at)}{h.note ? ` · ${h.note}` : ""}</div>
          ))}
          {!(history || []).length && <div className="text-xs text-muted-foreground">No history recorded.</div>}
        </div>
      )}
    </Card>
  );
}

function Row({ k, v }) {
  return <div className="flex justify-between gap-4"><dt className="text-muted-foreground">{k}</dt>
    <dd className={`text-right font-medium ${v ? "" : "text-muted-foreground italic"}`}>{NC(v)}</dd></div>;
}
